import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, Alert, Table, Tag, message } from 'antd';
import {
  ShoppingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import request from '../../api/request';
import './index.css';

const { Title } = Typography;

interface DashboardOverview {
  totalProducts: number;
  totalOrders: number;
  todayOrders: number;
  todaySuccess: number;
  todayFailed: number;
  todayPending: number;
  successRate: number;
  failedRate: number;
  exceptionCount: number;
  estimatedCommission: number;
}

interface TrendItem {
  date: string;
  total: number;
  success: number;
  failed: number;
}

interface PlatformItem {
  platform: string;
  total: number;
  success: number;
  failed: number;
  successRate: number;
}

interface FailureReasonItem {
  reason: string;
  count: number;
}

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [trends, setTrends] = useState<TrendItem[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformItem[]>([]);
  const [failureReasons, setFailureReasons] = useState<FailureReasonItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const [overviewData, trendData, platformData, failureData] = await Promise.all([
          request({ url: '/api/dashboard/overview', method: 'GET' }),
          request({ url: '/api/dashboard/order-trends', method: 'GET', params: { days: 7 } }),
          request({ url: '/api/dashboard/platform-stats', method: 'GET' }),
          request({ url: '/api/dashboard/failure-reasons', method: 'GET' }),
        ]);
        setOverview(overviewData as unknown as DashboardOverview);
        setTrends((trendData as unknown as TrendItem[]) || []);
        setPlatformStats((platformData as unknown as PlatformItem[]) || []);
        setFailureReasons((failureData as unknown as FailureReasonItem[]) || []);
      } catch (error: any) {
        message.error(error?.response?.data?.message || '获取仪表盘数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  return (
    <div className="dashboard-container">
      <Alert
        message="运营概览"
        description="这里展示实时订单、平台状态、异常订单和佣金预估。"
        type={overview?.exceptionCount ? 'warning' : 'success'}
        showIcon
        style={{ marginBottom: 24 }}
        closable
      />
      
      <Title level={2}>仪表板</Title>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic
              title="产品总数"
              value={overview?.totalProducts || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic
              title="今日订单"
              value={overview?.todayOrders || 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic
              title="今日成功率"
              value={overview?.successRate || 0}
              suffix="%"
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic
              title="今日佣金预估"
              value={overview?.estimatedCommission || 0}
              precision={2}
              prefix={<RiseOutlined />}
              suffix="元"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic title="今日失败" value={overview?.todayFailed || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic title="今日处理中" value={overview?.todayPending || 0} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic title="待处理异常" value={overview?.exceptionCount || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card className="statistic-card">
            <Statistic title="历史订单总数" value={overview?.totalOrders || 0} />
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} className="dashboard-charts">
        <Col xs={24} lg={12}>
          <Card title="近 7 天订单趋势" className="chart-card">
            <Table
              size="small"
              rowKey="date"
              dataSource={trends}
              loading={loading}
              pagination={false}
              columns={[
                { title: '日期', dataIndex: 'date' },
                { title: '订单', dataIndex: 'total' },
                { title: '成功', dataIndex: 'success' },
                { title: '失败', dataIndex: 'failed' },
              ]}
            />
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="平台同步状态" className="chart-card">
            <Table
              size="small"
              rowKey="platform"
              dataSource={platformStats}
              loading={loading}
              pagination={false}
              columns={[
                {
                  title: '平台',
                  dataIndex: 'platform',
                  render: (value) => <Tag color={value === '172' ? 'orange' : 'blue'}>{value}</Tag>,
                },
                { title: '订单', dataIndex: 'total' },
                { title: '成功', dataIndex: 'success' },
                { title: '失败', dataIndex: 'failed' },
                { title: '成功率', dataIndex: 'successRate', render: (value) => `${value}%` },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dashboard-charts">
        <Col xs={24}>
          <Card title="失败原因排行" className="chart-card">
            <Table
              size="small"
              rowKey="reason"
              dataSource={failureReasons}
              loading={loading}
              pagination={false}
              columns={[
                { title: '原因', dataIndex: 'reason', ellipsis: true },
                { title: '数量', dataIndex: 'count', width: 120 },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
